import type { AdminTopUpDto, TopUpStatus } from '@bravo/shared';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/DataTable';
import { Badge, Button, Empty, ErrorBlock, Field, PageHeader, Spinner, TextArea, TextInput, statusTone } from '../components/ui';
import { apiData, apiPage } from '../lib/api';
import { toastError, toastOk } from '../lib/feedback';
import { parseEtbField, useFormat } from '../lib/format';

const NEW_USER_MS = 7 * 24 * 60 * 60 * 1000;
const STATUSES: TopUpStatus[] = ['pending', 'approved', 'rejected'];

export function TopUpsPage() {
  const { t } = useTranslation();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<TopUpStatus>('pending');
  const [selected, setSelected] = useState<AdminTopUpDto | null>(null);
  const [credited, setCredited] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState<'approve' | 'reject' | null>(null);

  const query = useInfiniteQuery({
    queryKey: ['topups', status],
    queryFn: ({ pageParam }) =>
      apiPage<AdminTopUpDto>('/admin/topups', { status, limit: 20, cursor: pageParam }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = useMemo(
    () => query.data?.pages.flatMap((page) => page.items) ?? [],
    [query.data],
  );

  const duplicateNames = useMemo(() => {
    const counts = new Map<string, number>();
    for (const row of items) {
      const name = row.senderName?.trim().toLowerCase();
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, count]) => count > 1).map(([name]) => name));
  }, [items]);

  function suspicious(row: AdminTopUpDto): boolean {
    const fresh =
      Boolean(row.userCreatedAt) && Date.now() - Date.parse(row.userCreatedAt ?? '') < NEW_USER_MS;
    const name = row.senderName?.trim().toLowerCase();
    return fresh || Boolean(name && duplicateNames.has(name));
  }

  function open(row: AdminTopUpDto) {
    setSelected(row);
    setCredited(row.creditedAmount ? format.etbBare(row.creditedAmount) : format.etbBare(row.amountClaimed));
    setReason('');
  }

  async function approve() {
    if (!selected) return;
    setBusy('approve');
    try {
      const amount = parseEtbField(credited);
      await apiData(`/admin/topups/${selected.id}/approve`, {
        method: 'POST',
        body: amount === null ? {} : { creditedAmount: amount },
      });
      toastOk(t('topups.approvedToast'));
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['topups'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(null);
    }
  }

  async function reject() {
    if (!selected) return;
    setBusy('reject');
    try {
      await apiData(`/admin/topups/${selected.id}/reject`, {
        method: 'POST',
        body: { reason },
      });
      toastOk(t('topups.rejectedToast'));
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ['topups'] });
    } catch (error) {
      toastError(error, t('errorTitle'));
    } finally {
      setBusy(null);
    }
  }

  const columns: ColumnDef<AdminTopUpDto>[] = [
    {
      id: 'user',
      header: t('user'),
      cell: ({ row }) => row.original.userName || row.original.userPhone || row.original.userId,
    },
    { id: 'method', header: t('method'), cell: ({ row }) => format.method(row.original.method) },
    { id: 'amount', header: t('amount'), cell: ({ row }) => format.etb(row.original.amountClaimed) },
    { id: 'reference', header: t('topups.reference'), cell: ({ row }) => row.original.referenceId },
    { id: 'time', header: t('time'), cell: ({ row }) => format.when(row.original.createdAt) },
    {
      id: 'status',
      header: t('status'),
      cell: ({ row }) => (
        <Badge tone={statusTone(row.original.status)}>{format.status(row.original.status)}</Badge>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title={t('topups.title')} subtitle={t('topups.subtitle')} />
      <div className="mb-4 flex gap-2">
        {STATUSES.map((item) => (
          <Button
            key={item}
            variant={item === status ? 'primary' : 'secondary'}
            onClick={() => {
              setStatus(item);
              setSelected(null);
            }}
          >
            {format.status(item)}
          </Button>
        ))}
      </div>
      {query.isLoading ? <Spinner /> : null}
      {query.isError ? <ErrorBlock message={t('errorTitle')} onRetry={() => void query.refetch()} /> : null}
      {!query.isLoading && items.length === 0 ? <Empty title={t('topups.empty')} /> : null}
      {items.length > 0 ? (
        <DataTable
          columns={columns}
          data={items}
          onRow={open}
          rowClassName={(row) => (suspicious(row) ? 'bg-accent/10' : undefined)}
        />
      ) : null}
      {query.hasNextPage ? (
        <Button className="mt-4" variant="secondary" onClick={() => void query.fetchNextPage()}>
          {t('loadMore')}
        </Button>
      ) : null}

      {selected ? (
        <section className="mt-6 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-lg font-semibold">{t('details')}</h2>
          {suspicious(selected) ? (
            <p className="mt-2 text-sm text-accent-text">{t('topups.suspicious')}</p>
          ) : null}
          <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-fg-muted">{t('user')}</dt>
              <dd>
                <Link className="text-brand-text underline" to={`/users/${selected.userId}`}>
                  {selected.userName || selected.userPhone || selected.userId}
                </Link>
              </dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('topups.sender')}</dt>
              <dd>{selected.senderName || '—'}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('topups.reference')}</dt>
              <dd className="font-mono">{selected.referenceId}</dd>
            </div>
            <div>
              <dt className="text-fg-muted">{t('amount')}</dt>
              <dd>{format.etb(selected.amountClaimed)}</dd>
            </div>
          </dl>
          {selected.hasReceipt ? (
            <img
              src={`/api/v1/admin/topups/${selected.id}/receipt`}
              alt={t('topups.receipt')}
              className="mt-4 max-h-80 rounded-xl border border-border"
            />
          ) : (
            <p className="mt-4 text-sm text-fg-muted">{t('topups.noReceipt')}</p>
          )}
          {selected.status === 'pending' ? (
            <div className="mt-4 space-y-3">
              {selected.awaitingSecondApproval ? (
                <p className="text-sm text-accent-text">{t('topups.second')}</p>
              ) : null}
              <Field label={t('topups.credited')}>
                <TextInput
                  inputMode="decimal"
                  value={credited}
                  disabled={selected.awaitingSecondApproval}
                  onChange={(event) => setCredited(event.target.value)}
                />
              </Field>
              <Field label={t('reason')}>
                <TextArea value={reason} onChange={(event) => setReason(event.target.value)} />
              </Field>
              <div className="flex gap-2">
                <Button data-testid="topup-approve" loading={busy === 'approve'} onClick={() => void approve()}>
                  {t('topups.approve')}
                </Button>
                <Button
                  variant="danger"
                  loading={busy === 'reject'}
                  disabled={reason.trim().length < 3}
                  onClick={() => void reject()}
                >
                  {t('topups.reject')}
                </Button>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
